import { isDefined } from "@homebound/activesupport";
import {
  column,
  Css,
  GridColumn,
  GridDataRow,
  GridTable,
  Icon,
  numericColumn,
  RowStyles,
  simpleHeader,
} from "@homebound/beam";
import { useMemo } from "react";
import { CostType } from "src/generated/graphqlTypes";
import { createProductOfferingScopeUrl } from "src/RouteUrls";
import { openNewTab } from "src/utils/window";
import {
  PlanBasedBidsTableV2_BidPlanChildGroupScopedLineFragment,
  PlanBasedBidsTableV2_ScopeTakeoffLineItemFragment,
} from "./PlanBasedBidsTableV2.generated";

type PlanBasedBidsUnmatchedScopeTableProps = {
  scopedLineItems: PlanBasedBidsTableV2_BidPlanChildGroupScopedLineFragment[];
};

export function PlanBasedBidsUnmatchedScopeTable(props: PlanBasedBidsUnmatchedScopeTableProps) {
  const { scopedLineItems } = props;
  const unmatchedScopeLines = useMemo(
    () =>
      scopedLineItems
        // Pull the takeoff line items from the single unmatched bucket (groupLine == null)
        .filter((sli) => !isDefined(sli.groupLine))
        .flatMap((sli) => sli.takeoffLineItems),
    [scopedLineItems],
  );
  if (unmatchedScopeLines.isEmpty) return null;
  return <PlanBasedBidsUnmatchedScopeTableView unmatchedScopeLines={unmatchedScopeLines} />;
}

type PlanBasedBidsUnmatchedScopeTableViewProps = {
  unmatchedScopeLines: PlanBasedBidsTableV2_ScopeTakeoffLineItemFragment[];
};

type HeaderRow = { kind: "header"; data: undefined };
type DataRow = { kind: "data"; data: PlanBasedBidsTableV2_ScopeTakeoffLineItemFragment };
type Row = HeaderRow | DataRow;

function PlanBasedBidsUnmatchedScopeTableView(props: PlanBasedBidsUnmatchedScopeTableViewProps) {
  const { unmatchedScopeLines } = props;
  const rows = useMemo(() => createRows(unmatchedScopeLines), [unmatchedScopeLines]);
  const columns = useMemo(() => createColumns(), []);
  const rowStyles = useMemo(() => createRowStyles(), []);
  return (
    <div css={Css.mb2.$}>
      <div css={Css.df.aic.gap1.p1.$}>
        <Icon icon="error" />
        <span css={Css.smSb.gray800.$}>Added scope that doesn’t map to any of your existing bid lines</span>
      </div>
      <GridTable id="unmatchedScopeTable" columns={columns} rows={rows} rowStyles={rowStyles} />
    </div>
  );
}

function createRowStyles(): RowStyles<Row> {
  return {
    data: {
      onClick: ({ data: tli }) => {
        const url = createProductOfferingScopeUrl(tli.readyPlan.id, tli.readyPlan.version.id);
        const params = new URLSearchParams({
          search: tli.materialVariant?.code ?? tli.item.fullCode,
          filter: JSON.stringify({ costCode: [tli.item.costCode.id] }),
        });
        openNewTab(`${url}?${params.toString()}`);
      },
    },
  };
}

function createColumns(): GridColumn<Row>[] {
  return [
    column<Row>({ header: "Item Code + Name", data: (tli) => `${tli.item.fullCode} ${tli.item.name}` }),
    column<Row>({ header: "Name", data: (tli) => tli.name }),
    column<Row>({ header: "Cost Type", data: (tli) => tli.costTypeDetail.name }),
    column<Row>({ header: "Location", data: (tli) => tli.location.displayLocationPath }),
    column<Row>({
      header: "Material/Labor",
      // Labor lines have no material variant, so just label them "Labor"; everything else shows its material code
      data: (tli) => (tli.costTypeDetail.code === CostType.Labor ? "Labor" : tli.materialVariant?.code),
    }),
    numericColumn<Row>({
      header: "QTY",
      // "N/A" for units that ignore quantity (e.g. Lump Sum/Allowance), matching the takeoff scope table
      data: (tli) => (tli.unitOfMeasure.useQuantity ? (tli.quantity ?? "") : "N/A"),
    }),
    column<Row>({ header: "UoM", data: (tli) => tli.unitOfMeasure.abbreviation }),
    column<Row>({
      header: "Option",
      // Combine each assigned option as "Name (code)"; blank when the TLI has no options
      data: (tli) => tli.options.map((o) => `${o.name} (${o.code})`).join(", "),
    }),
  ];
}

function createRows(unmatchedScopeLines: PlanBasedBidsTableV2_ScopeTakeoffLineItemFragment[]): GridDataRow<Row>[] {
  return [simpleHeader, ...unmatchedScopeLines.map((tli) => ({ kind: "data" as const, id: tli.id, data: tli }))];
}
